import React from 'react';
import { useDispatch } from 'react-redux';
import { globalMessage } from '@/utils/globalMessage';
import { ReviewAttachmentUploadView } from '@/components/appSettings/FileUpload/ReviewAttachmentUploadView';
import { FileStats, RunwayResponseType } from '@/types';
import { Nullable, StepRunComponentProps } from '@/typescriptHelpers';
import { StepRunUpdatable } from '@/actions/stepRun';
import { ReleaseUpdatable } from '@/actions/updatable';
import { tryUpdateRelease } from '@/actions/release';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface ReleaseReviewAttachmentUploadProps {
    orgId: string;
    appId: string;
    releaseId: string;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    currentAttachment?: FileStats;
}

// Uploads/removes the review attachment for this release only. Unlike DefaultReviewAttachmentUpload,
// this does not touch the app-level default used by the automation.
function ReleaseReviewAttachmentUpload({
    orgId,
    appId,
    releaseId,
    stepRunComponentProps,
    currentAttachment,
}: ReleaseReviewAttachmentUploadProps) {
    const dispatch = useDispatch();

    const [uploadForStepRun, uploadIsLoadingStepRun, uploadResponseTypeStepRun, uploadErrorStepRun] = useCallUpdatable(
        StepRunUpdatable.UploadReviewAttachment,
        stepRunComponentProps,
    );
    const [deleteForStepRun, deleteIsLoadingStepRun, deleteResponseTypeStepRun, deleteErrorStepRun] = useCallUpdatable(
        StepRunUpdatable.DeleteReviewAttachment,
        stepRunComponentProps,
    );

    const uploadIsLoadingReleaseStep = useIsLoading(ReleaseUpdatable.UploadReviewAttachment, releaseId);
    const uploadSuccessReleaseStep = useUpdated(ReleaseUpdatable.UploadReviewAttachment, releaseId);
    const uploadErrorReleaseStep = useError(ReleaseUpdatable.UploadReviewAttachment, releaseId);
    const deleteIsLoadingReleaseStep = useIsLoading(ReleaseUpdatable.DeleteReviewAttachment, releaseId);
    const deleteSuccessReleaseStep = useUpdated(ReleaseUpdatable.DeleteReviewAttachment, releaseId);
    const deleteErrorReleaseStep = useError(ReleaseUpdatable.DeleteReviewAttachment, releaseId);

    const isUploading = stepRunComponentProps ? uploadIsLoadingStepRun : uploadIsLoadingReleaseStep;
    const isDeleting = stepRunComponentProps ? deleteIsLoadingStepRun : deleteIsLoadingReleaseStep;
    const uploadSuccess =
        stepRunComponentProps ? uploadResponseTypeStepRun === RunwayResponseType.Success : uploadSuccessReleaseStep;
    const deleteSuccess =
        stepRunComponentProps ? deleteResponseTypeStepRun === RunwayResponseType.Success : deleteSuccessReleaseStep;
    const uploadError = stepRunComponentProps ? uploadErrorStepRun : uploadErrorReleaseStep;
    const deleteError = stepRunComponentProps ? deleteErrorStepRun : deleteErrorReleaseStep;

    React.useEffect(() => {
        if (uploadSuccess) {
            globalMessage.success('Review attachment uploaded!');
        }
    }, [uploadSuccess]);

    React.useEffect(() => {
        if (deleteSuccess) {
            globalMessage.success('Review attachment removed!');
        }
    }, [deleteSuccess]);

    React.useEffect(() => {
        if (uploadError) {
            globalMessage.error(uploadError);
        }
    }, [uploadError]);

    React.useEffect(() => {
        if (deleteError) {
            globalMessage.error(deleteError);
        }
    }, [deleteError]);

    const handleUpload = (file: File) => {
        if (stepRunComponentProps) {
            uploadForStepRun(file);
        } else {
            dispatch(
                tryUpdateRelease(ReleaseUpdatable.UploadReviewAttachment, { orgId, appId, releaseId, file }, releaseId),
            );
        }
    };

    const handleDelete = () => {
        if (stepRunComponentProps) {
            deleteForStepRun();
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.DeleteReviewAttachment, { orgId, appId, releaseId }, releaseId));
        }
    };

    return (
        <ReviewAttachmentUploadView
            description="Upload a file attachment to include in the review information for this release."
            attachment={currentAttachment}
            isUploading={isUploading}
            isDeleting={isDeleting}
            handleUpload={handleUpload}
            handleDelete={handleDelete}
        />
    );
}

export default ReleaseReviewAttachmentUpload;
